/** as we query financial config to initialise the extensions, we might as well store it in a context so we do not have to query again
 *  same goes for subscriptions and customer, we also need a way to keep the various components up to date
 */

import { CreditCard, Customer, DirectDebit, ProviderSelection } from '@fox/finance';
import { sendUpdate, useSubscribeTopicWithHandler } from 'fox-extensions';
import { createContext, useContext, useRef, useState } from 'react';
import {
	LineItem,
	SwiftFoxSubscription,
	SwiftFoxSubscriptionLineItemResult,
	SwiftFoxSubscriptionResult
} from './Membership/financial-details/contracts';
import { useCustomer, useSubscriptions } from './Membership/query-hooks/query-implementations';
import { useContentContext } from './content-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL } from './topics';

type FinancialContext = {
	getCustomers: (org: boolean) => Customer[];
	getCreditCard: (paymentMethodId: string, org: boolean) => CreditCard;
	getDirectDebit: (paymentMethodId: string, org: boolean) => DirectDebit;
	getSubscription: (subscriptionId: string) => SwiftFoxSubscription;
	getSubscriptions: (org: boolean, allowedPlanCategories?: string[]) => SwiftFoxSubscription[];
	getPayableLineItemsForSubscription: (subscriptionId: string, useLinkedOrganisationData: boolean) => LineItem[];
	getAllPayableLineItems: (useLinkedOrganisationData: boolean, allowedPlanCategories?: string[]) => SwiftFoxSubscriptionLineItemResult[];
	providers: ProviderSelection[];
	subscriptionsError: string;
	orgSubscriptionsError: string;
	customerError: string;
	orgCustomerError: string;
	payableLineItemsError: string;
	orgPayableLineItemsError: string;
	customerLoading: boolean;
	orgCustomerLoading: boolean;
	subscriptionsLoading: boolean;
	orgSubscriptionsLoading: boolean;
	payableLineItemsLoading: boolean;
	orgPayableLineItemsLoading: boolean;
};

const Context = createContext<FinancialContext>(undefined);
Context.displayName = 'FinancialConfigContext';

export function FinancialContextProvider({
	children,
	providers,
	useLinkedOrganisationSubscriptionData,
	usePersonSubscriptionData,
	useLinkedOrganisationCustomerData,
	usePersonCustomerData
}: {
	providers: ProviderSelection[];
	usePersonSubscriptionData: boolean;
	useLinkedOrganisationSubscriptionData: boolean;
	useLinkedOrganisationCustomerData: boolean;
	usePersonCustomerData: boolean;
	children: React.ReactNode;
}) {
	const { contentId, requestToken, isLoggedIn } = useContentContext();

	const [payableLineItemsLoading, setPayableLineItemsLoading] = useState<boolean>(false);
	const [orgPayableLineItemsLoading, setOrgPayableLineItemsLoading] = useState<boolean>(false);
	const [payableLineItemsError, setPayableLineItemsError] = useState<string>('');
	const [orgPayableLineItemsError, setOrgPayableLineItemsError] = useState<string>('');

	const customerDataRef = useRef<Customer[]>(undefined);
	const orgCustomerDataRef = useRef<Customer[]>(undefined);
	const subscriptionsResultRef = useRef<SwiftFoxSubscriptionResult>(undefined);
	const orgSubscriptionsResultRef = useRef<SwiftFoxSubscriptionResult>(undefined);
	const subscriptionPayableLineItemsRef = useRef<SwiftFoxSubscriptionLineItemResult[]>([]);
	const orgSubscriptionPayableLineItemsRef = useRef<SwiftFoxSubscriptionLineItemResult[]>([]);

	const payableLineItems = async (subscriptions: SwiftFoxSubscription[], useLinkedOrganisationData: boolean) => {
		if (useLinkedOrganisationCustomerData) {
			setPayableLineItemsLoading(true);
			setPayableLineItemsError('');
		} else {
			setOrgPayableLineItemsLoading(true);
			setOrgPayableLineItemsError('');
		}

		let url = `/Umbraco/Api/Membership/SubscriptionsPayableLineItems?contentId=${contentId}`;
		if (subscriptions?.length) {
			url += subscriptions.map(sub => `&subscriptionIds=${encodeURIComponent(sub.id)}`).join('');
		}
		const res = await fetch(url, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				RequestVerificationToken: requestToken
			}
		});
		if (res.ok) {
			const response = await res.json();
			if (response.success === true) {
				if (useLinkedOrganisationData) {
					orgSubscriptionPayableLineItemsRef.current = response.result;
				} else {
					subscriptionPayableLineItemsRef.current = response.result;
				}
			} else {
				const err = response.message;
				if (useLinkedOrganisationCustomerData) {
					setPayableLineItemsLoading(err);
				} else {
					setOrgPayableLineItemsLoading(err);
				}
			}
		}

		if (useLinkedOrganisationCustomerData) {
			setPayableLineItemsLoading(false);
		} else {
			setOrgPayableLineItemsLoading(false);
		}
	};

	const {
		error: customerError,
		loading: customerLoading,
		refetch: customerRefetch
	} = useCustomer({
		contentId,
		requestToken,
		skip: !isLoggedIn || !providers.length || !usePersonCustomerData,
		onFetchComplete: (data: Customer[]) => {
			customerDataRef.current = data;
		}
	});
	const {
		error: orgCustomerError,
		loading: orgCustomerLoading,
		refetch: orgCustomerRefetch
	} = useCustomer({
		contentId,
		requestToken,
		useLinkedOrganisationData: true,
		skip: !isLoggedIn || !providers.length || !useLinkedOrganisationCustomerData,
		onFetchComplete: (data: Customer[]) => {
			orgCustomerDataRef.current = data;
		}
	});

	const {
		error: subscriptionsError,
		loading: subscriptionsLoading,
		refetch: subscriptionsRefetch
	} = useSubscriptions({
		contentId,
		requestToken,
		skip: !isLoggedIn || !providers.length || !usePersonSubscriptionData,
		onFetchCompleteAsync: async (data: SwiftFoxSubscriptionResult) => {
			subscriptionsResultRef.current = data;
			await payableLineItems(data?.items ?? [], false);
		}
	});

	const {
		error: orgSubscriptionsError,
		loading: orgSubscriptionsLoading,
		refetch: orgSubscriptionsRefetch
	} = useSubscriptions({
		contentId,
		requestToken,
		useLinkedOrganisationData: true,
		skip: !isLoggedIn || !providers.length || !useLinkedOrganisationSubscriptionData,
		onFetchCompleteAsync: async (data: SwiftFoxSubscriptionResult) => {
			orgSubscriptionsResultRef.current = data;
			await payableLineItems(data?.items ?? [], true);
		}
	});

	const getCustomers = (org: boolean) => (org ? orgCustomerDataRef.current : customerDataRef.current);
	const getCreditCard = (paymentMethodId: string, org: boolean): CreditCard =>
		(org ? orgCustomerDataRef : customerDataRef).current.flatMap(c => c.creditCards).find(c => c.id === paymentMethodId);
	const getDirectDebit = (paymentMethodId: string, org: boolean): DirectDebit =>
		(org ? orgCustomerDataRef : customerDataRef).current.flatMap(c => c.directDebits).find(c => c.id === paymentMethodId);
	const getSubscription = (subscriptionId: string): SwiftFoxSubscription =>
		subscriptionsResultRef.current.items.find(s => s.id === subscriptionId);

	const filterByAllowedPlans = (subscriptions: SwiftFoxSubscription[], allowedPlanCategories?: string[]) => {
		return subscriptions.filter(sub => {
			if (allowedPlanCategories?.length && !allowedPlanCategories.includes(sub.fee?.plan?.category?.toLowerCase())) {
				return false;
			}
			return true;
		});
	};

	const getSubscriptions = (org: boolean, allowedPlanCategories?: string[]) => {
		return filterByAllowedPlans((org ? orgSubscriptionsResultRef : subscriptionsResultRef).current?.items ?? [], allowedPlanCategories);
	};
	const getPayableLineItemsForSubscription = (subscriptionId: string, useLinkedOrganisationData: boolean) =>
		(useLinkedOrganisationData ? orgSubscriptionPayableLineItemsRef.current : subscriptionPayableLineItemsRef.current)?.find(
			s => s.subscriptionId === subscriptionId
		)?.payableLineItems ?? [];

	const getAllPayableLineItems = (useLinkedOrganisationData: boolean, allowedPlanCategories?: string[]) => {
		const payableLineItems = useLinkedOrganisationData
			? orgSubscriptionPayableLineItemsRef.current
			: subscriptionPayableLineItemsRef.current;

		const filteredPayableLineItems = payableLineItems?.filter(p => {
			const sub = (useLinkedOrganisationData ? orgSubscriptionsResultRef.current : subscriptionsResultRef.current)?.items?.find(
				s => s.id === p.subscriptionId
			);
			if (!sub) return false;
			if (allowedPlanCategories?.length && !allowedPlanCategories.includes(sub.fee?.plan?.category?.toLowerCase())) {
				return false;
			}
			return true;
		});

		return filteredPayableLineItems;
	};

	useSubscribeTopicWithHandler(REFETCH_FINANCIAL, async () => {
		if (usePersonCustomerData) {
			await customerRefetch();
		}
		if (useLinkedOrganisationCustomerData) {
			await orgCustomerRefetch();
		}
		if (usePersonSubscriptionData) {
			await subscriptionsRefetch();
		}
		if (useLinkedOrganisationSubscriptionData) {
			await orgSubscriptionsRefetch();
		}
		sendUpdate(FINANCIAL_DATA_FETCHED);
	});

	const contextValue: FinancialContext = {
		providers,
		getCreditCard,
		getCustomers,
		subscriptionsError,
		orgSubscriptionsError,
		customerError,
		getDirectDebit,
		getSubscription,
		getSubscriptions,
		customerLoading,
		subscriptionsLoading,
		orgSubscriptionsLoading,
		orgCustomerError,
		orgCustomerLoading,
		getPayableLineItemsForSubscription,
		payableLineItemsError,
		payableLineItemsLoading,
		orgPayableLineItemsError,
		orgPayableLineItemsLoading,
		getAllPayableLineItems
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useFinancialContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing financial context');

	return context;
}
